import type { DocumentDetails, DocumentSummary } from '../types/api';

export interface DeliveryPresentation {
  label: 'NOT ATTEMPTED' | 'LOCAL ONLY' | 'DELIVERED' | 'DELIVERY FAILED';
  detail: string;
  destination?: string;
}

type DeliveryInput = Pick<DocumentSummary, 'latestDelivery'> & Partial<Pick<DocumentDetails, 'attempts'>>;

export function describeDocumentDelivery(document: DeliveryInput): DeliveryPresentation {
  const delivery = document.latestDelivery ?? deliveryFromAttempts(document.attempts);
  if (!delivery) {
    return {
      label: 'NOT ATTEMPTED',
      detail: 'The document is generated, but no delivery attempt is recorded.',
    };
  }

  const destination = delivery.destination;
  if (delivery.transport === 'LOCAL' || delivery.mode === 'logical') {
    return {
      label: 'LOCAL ONLY',
      detail: delivery.message ?? 'Stored locally; the document was not sent to the external partner.',
      destination,
    };
  }

  // 'SUCCESS' is the IFTMAN/edi_messages path's own vocabulary (ediAttemptStatusCase() in
  // postgres-document.repository.ts, matching document_attempts.status) -- a real successful AS2
  // delivery (transport_status=RECEIPT_RECEIVED) was showing as "DELIVERY FAILED" here because this
  // check only recognized the generic documents path's DELIVERED/ACCEPTED vocabulary.
  if (delivery.status === 'DELIVERED' || delivery.status === 'ACCEPTED' || delivery.status === 'SUCCESS') {
    return {
      label: 'DELIVERED',
      detail: delivery.message ?? `Delivered using ${delivery.transport ?? 'the configured connector'}.`,
      destination,
    };
  }

  return {
    label: 'DELIVERY FAILED',
    detail: delivery.message ?? delivery.reason ?? 'The latest external delivery attempt failed.',
    destination,
  };
}

function deliveryFromAttempts(attempts: DocumentDetails['attempts'] | undefined): DocumentSummary['latestDelivery'] {
  const latest = attempts?.find((attempt) => attempt.attemptType === 'RESEND');
  if (!latest) return undefined;

  const raw = latest.details['delivery'];
  const delivery = isRecord(raw) ? raw : {};
  const read = (key: string): string | undefined => typeof delivery[key] === 'string' ? delivery[key] : undefined;
  return {
    status: read('status') ?? latest.status,
    transport: read('transport'),
    mode: read('mode'),
    destination: read('destination'),
    message: read('message'),
    reason: latest.reason,
    attemptedAt: read('attemptedAt'),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}
